import React from "react";
import { Icon } from "./registry";
import type { SiteBundle, SiteContent } from "./website/types";
import { toE164 } from "./website/util";
import { sampleBundle } from "./website/sample";
import { Wizard } from "./website/wizard";
import { SiteEditor } from "./website/editor";
/* ============================================================
   Fence Works — Website (Growth plan)
   The contractor's own website, generated from their account and hosted on
   their own domain (docs/SITES_CONTRACT.md §7, WEBSITE_BUILDER_PLAN.md §6.9):
   • no site yet → a four-step wizard, then POST /sites/me builds the draft
   • a site      → the editor (website/editor.tsx): section tabs on the left,
                   the live preview on the right, autosave, publish, history,
                   the domain connector and the website's leads
   Without a service (sandbox, local dev) it runs on a sample site: edits stay
   on screen, and anything that would save or publish says so.
   Pieces: website/{types,util,sample,fields,preview,tabs,domain,wizard,editor}.
   ============================================================ */

/* the wizard's answers, applied to the sample so the sandbox can "build" */
function applyToSample(d: SiteContent, basics: any, ans: any): SiteContent {
  const out: SiteContent = JSON.parse(JSON.stringify(d));
  if (basics) {
    if (basics.name) { out.business.name = basics.name; out.business.shortName = basics.name.replace(/\s+(LLC|Inc\.?|Co\.?)$/i, "") || basics.name; }
    if (basics.phone) out.business.phone = { display: basics.phone, e164: toE164(basics.phone) };
    if (basics.email) out.business.email = basics.email;
    if (basics.city) out.business.address.city = basics.city;
    if (basics.state) out.business.address.region = basics.state;
  }
  if (ans) {
    if (ans.colors) out.brand.colors = { ...ans.colors };
    if (ans.font) out.brand.font = ans.font;
    if (Array.isArray(ans.styles)) out.styles.items = out.styles.items.map((s) => ({ ...s, on: ans.styles.includes(s.slug) }));
    out.claims.permitsHandled = !!ans.permitsHandled;
    if (ans.warrantyText) out.claims.warrantyText = ans.warrantyText;
    if (ans.postMethod) out.claims.postMethod = ans.postMethod;
    out.business.insured = !!ans.insured;
    if (ans.license) out.business.license = ans.license;
    if (ans.ownerName) out.business.ownerName = ans.ownerName;
    if (Array.isArray(ans.differentiators) && ans.differentiators.length) out.home.why = ans.differentiators.map((t: string) => ({ t, d: "" })).concat(out.home.why).slice(0, 4);
  }
  return out;
}

function WebsitePage({ store, go, params, auth }: any) {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [bundle, setBundle] = React.useState<SiteBundle | null>(() => (live ? null : sampleBundle(store)));
  const [err, setErr] = React.useState<any>(null);
  const [sampleWizard, setSampleWizard] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState(0);

  const load = React.useCallback(async () => {
    if (!live) return;
    try {
      setErr(null);
      const r = await DB.siteGet();
      setBundle({ ...r, site: r.site || null, domains: r.domains || [] });
    } catch (e: any) {
      const code = e.code === "plan" || e.status === 403 && /plan/i.test(e.message || "") ? "plan"
        : e.code === "migration" || e.status === 503 ? "migration" : "other";
      setErr({ code, message: String(e.message || e) });
    }
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  /* after POST /sites/me: open the editor; the wizard's own name/phone/email
     edits (the service builds from Company settings) go in as a first autosave */
  const onCreated = (b: SiteBundle | null, basics: any, answers: any) => {
    if (!live) {
      setBundle((cur) => { const base = cur || sampleBundle(store); return { ...base, site: { ...base.site!, draft: applyToSample(base.site!.draft, basics, answers) } }; });
      setSampleWizard(false); setEditorKey((k) => k + 1);
      return;
    }
    if (!b || !b.site) { load(); return; }
    const d = b.site.draft;
    if (basics && d && d.business) {
      const nd = JSON.parse(JSON.stringify(d));
      if (basics.name && basics.name !== nd.business.name) nd.business.name = basics.name;
      if (basics.phone && basics.phone !== nd.business.phone.display) nd.business.phone = { display: basics.phone, e164: toE164(basics.phone) };
      if (basics.email && basics.email !== nd.business.email) nd.business.email = basics.email;
      // the draft is edited locally; the editor sees it differs from the saved copy and autosaves it
      setBundle({ ...b, site: { ...b.site, draft: nd, _saved: d } as any });
    } else setBundle(b);
    setEditorKey((k) => k + 1);
  };

  const header = (
    <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
      <div>
        <span className="kicker kicker-teal">Marketing</span>
        <h1 className="display" style={{ fontSize: 28, margin: "6px 0 4px" }}>Website</h1>
        <p className="muted" style={{ margin: 0, maxWidth: 660 }}>A contractor website built from your account, hosted for you on your own domain, with every quote request landing in Customers.</p>
      </div>
    </div>
  );

  let content: React.ReactNode;
  if (err && err.code === "plan") {
    content = (<>{header}
      <div className="card pad ws-gate">
        <span className="chip teal">Growth plan</span>
        <h2 className="display" style={{ fontSize: 22, margin: "10px 0 6px" }}>Your website is part of Growth</h2>
        <p className="muted" style={{ margin: "0 auto 16px", maxWidth: 460 }}>{err.message && !/^\/sites/.test(err.message) ? err.message : "Upgrade to Growth and we'll build, host and keep your website fresh — on your own domain."}</p>
        <button type="button" className="btn btn-primary" onClick={() => go("account")}><Icon name="bolt" size={15} />See Growth</button>
      </div></>);
  } else if (err && err.code === "migration") {
    content = (<>{header}
      <div className="card pad" style={{ borderColor: "var(--amber-tint)" }}>
        <b>One database update to go.</b>
        <p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>The website builder needs <code>supabase/migrate_2026-09-29_sites.sql</code> run once. Everything else keeps working in the meantime.</p>
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={load}><Icon name="undo" size={13} />Check again</button>
      </div></>);
  } else if (err) {
    content = (<>{header}
      <div className="card pad">
        <b>Couldn't load your website.</b>
        <p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>{err.message}</p>
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={load}><Icon name="undo" size={13} />Try again</button>
      </div></>);
  } else if (!bundle) {
    content = <>{header}<div className="card pad muted"><span className="ws-spin" style={{ marginRight: 8 }} />Loading your website…</div></>;
  } else if (!bundle.site || sampleWizard) {
    content = <>{header}<Wizard store={store} auth={auth} live={live} onCreated={onCreated} onCancel={sampleWizard ? () => setSampleWizard(false) : undefined} /></>;
  } else {
    content = <SiteEditor key={editorKey} bundle={bundle} setBundle={setBundle} live={live} store={store} go={go} params={params} reload={load} onReplayWizard={!live ? () => setSampleWizard(true) : undefined} />;
  }
  return <div className="page fade-up ws-page">{content}</div>;
}

Object.assign(window, { WebsitePage });
export { WebsitePage };
